import { useSyncExternalStore } from 'react';

// Hide every corner notice while a blocking pop-up (the session-ended prompt) is
// up (ENG-4930 item 6). Same shape as `device-pane.ts`: a module-level flag the
// panel reads through `useSyncExternalStore`. Hidden depot notices unmount, so
// their auto-close timers restart when they return. The restore notice stays
// mounted in the hidden stack to preserve its component-owned dismissal.
const holds = new Set<symbol>();
const listeners = new Set<() => void>();
const emit = (): void => {
  for (const listener of listeners) listener();
};

/**
 * Hide the notice stack until the returned release function is called. Holds
 * nest: the stack shows again once the last hold releases.
 *
 * @returns Release function; calling it more than once is harmless.
 */
export const hideNotificationStack = (): (() => void) => {
  const key = Symbol();
  holds.add(key);
  emit();
  return () => {
    if (holds.delete(key)) emit();
  };
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Is the notice stack currently hidden? */
export const isNotificationStackHidden = (): boolean => holds.size > 0;

/** Reactive form of `isNotificationStackHidden` for the panel. */
export const useNotificationStackHidden = (): boolean =>
  useSyncExternalStore(subscribe, isNotificationStackHidden, isNotificationStackHidden);
